import { initializeOnApproach } from "../../../resources/js/_initialize-on-approach";

document.addEventListener("DOMContentLoaded", function () {
  const accordions = document.querySelectorAll(".image-accordion");

  accordions.forEach((accordion) => {
    initializeOnApproach(accordion, () => {
      initializeImageAccordionPatternReveal(accordion);
      initializeImageAccordionFrame(accordion);
      initializeImageAccordion(accordion);
    });
  });
});

document.addEventListener("blockEditorInit", function (event) {
  const block = event.detail.block;

  if (!block) {
    return;
  }

  if (block.matches && block.matches(".image-accordion")) {
    initializeImageAccordionPatternReveal(block);
    initializeImageAccordionFrame(block);
    initializeImageAccordion(block);
  }

  block.querySelectorAll(".image-accordion").forEach((accordion) => {
    initializeImageAccordionPatternReveal(accordion);
    initializeImageAccordionFrame(accordion);
    initializeImageAccordion(accordion);
  });
});

function initializeImageAccordionPatternReveal(blockElement) {
  const patternElement = blockElement.querySelector(".section-pattern__image");

  if (
    !patternElement ||
    blockElement.dataset.imageAccordionPatternInitialized === "true"
  ) {
    return;
  }

  blockElement.dataset.imageAccordionPatternInitialized = "true";

  if (!("IntersectionObserver" in window)) {
    blockElement.classList.add("image-accordion--pattern-visible");
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        blockElement.classList.add("image-accordion--pattern-visible");
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.25,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  observer.observe(blockElement);
}

function initializeImageAccordionFrame(blockElement) {
  const frameElement = blockElement.querySelector(".image-accordion__frame");
  const animatedPath = blockElement.querySelector(
    ".image-accordion__frame-animated-path",
  );

  if (
    !frameElement ||
    !animatedPath ||
    blockElement.dataset.imageAccordionFrameInitialized === "true"
  ) {
    return;
  }

  blockElement.dataset.imageAccordionFrameInitialized = "true";

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  let observer = null;
  let revealTimeout = null;

  if (prefersReducedMotion) {
    blockElement.classList.add("image-accordion--frame-complete");
    return;
  }

  animatedPath.addEventListener("animationend", function (event) {
    if (event.animationName !== "imageAccordionFrameDraw") {
      return;
    }

    blockElement.classList.remove("image-accordion--frame-animating");
    blockElement.classList.add("image-accordion--frame-complete");
  });

  const revealFrame = () => {
    if (
      blockElement.classList.contains("image-accordion--frame-animating") ||
      blockElement.classList.contains("image-accordion--frame-complete")
    ) {
      return;
    }

    blockElement.classList.add("image-accordion--frame-animating");
  };

  if (!("IntersectionObserver" in window)) {
    revealFrame();
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (
          blockElement.classList.contains("image-accordion--frame-animating") ||
          blockElement.classList.contains("image-accordion--frame-complete")
        ) {
          if (observer) {
            observer.disconnect();
          }
          return;
        }

        if (entry.isIntersecting) {
          if (revealTimeout) {
            return;
          }

          revealTimeout = window.setTimeout(() => {
            revealFrame();

            if (observer) {
              observer.disconnect();
            }

            revealTimeout = null;
          }, 1000);

          return;
        }

        if (revealTimeout) {
          window.clearTimeout(revealTimeout);
          revealTimeout = null;
        }
      });
    },
    {
      threshold: 0.35,
      rootMargin: "0px 0px -10% 0px",
    },
  );

  observer.observe(frameElement);
}

function initializeImageAccordion(blockElement) {
  const imageFadeDurationMs = 420;
  const items = Array.from(
    blockElement.querySelectorAll("[data-image-accordion-item]"),
  );

  if (
    items.length === 0 ||
    blockElement.dataset.imageAccordionInitialized === "true"
  ) {
    return;
  }

  const mediaItems = Array.from(
    blockElement.querySelectorAll("[data-image-accordion-media]"),
  );
  const triggers = items
    .map((item) => item.querySelector("[data-image-accordion-trigger]"))
    .filter(Boolean);

  if (triggers.length === 0) {
    return;
  }

  blockElement.dataset.imageAccordionInitialized = "true";
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  let currentActiveIndex = -1;

  const clearPanelTransition = (panel) => {
    if (!panel) {
      return;
    }

    if (panel.__imageAccordionRaf) {
      window.cancelAnimationFrame(panel.__imageAccordionRaf);
      panel.__imageAccordionRaf = null;
    }

    if (panel.__imageAccordionTransitionHandler) {
      panel.removeEventListener(
        "transitionend",
        panel.__imageAccordionTransitionHandler,
      );
      panel.__imageAccordionTransitionHandler = null;
    }
  };

  const setPanelState = (panel, expanded, immediate = false) => {
    if (!panel) {
      return;
    }

    clearPanelTransition(panel);

    if (immediate || prefersReducedMotion) {
      panel.classList.toggle("is-open", expanded);
      panel.hidden = !expanded;
      panel.style.height = "";
      panel.style.overflow = "";
      return;
    }

    const wasHidden = panel.hidden;

    panel.hidden = false;

    const startHeight = wasHidden
      ? 0
      : panel.getBoundingClientRect().height || 0;

    panel.style.overflow = "hidden";
    panel.style.height = `${startHeight}px`;
    panel.offsetHeight;

    panel.classList.toggle("is-open", expanded);

    const targetHeight = expanded ? panel.scrollHeight : 0;

    panel.__imageAccordionRaf = window.requestAnimationFrame(() => {
      panel.style.height = `${targetHeight}px`;
    });

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== "height") {
        return;
      }

      panel.removeEventListener("transitionend", handleTransitionEnd);
      panel.__imageAccordionTransitionHandler = null;

      if (expanded) {
        panel.style.height = "auto";
      } else {
        panel.hidden = true;
        panel.style.height = "";
      }

      panel.style.overflow = "";
    };

    panel.__imageAccordionTransitionHandler = handleTransitionEnd;
    panel.addEventListener("transitionend", handleTransitionEnd);
  };

  const clearMediaTransition = (mediaItem) => {
    if (!mediaItem || !mediaItem.__imageAccordionFadeTimeout) {
      return;
    }

    window.clearTimeout(mediaItem.__imageAccordionFadeTimeout);
    mediaItem.__imageAccordionFadeTimeout = null;
  };

  const setActiveItem = (activeIndex, immediate = false) => {
    if (!immediate && activeIndex === currentActiveIndex) {
      return;
    }

    items.forEach((item, index) => {
      const trigger = item.querySelector("[data-image-accordion-trigger]");
      const panel = item.querySelector("[data-image-accordion-panel]");
      const mediaItem = mediaItems[index];
      const isActive = index === activeIndex;

      item.classList.toggle("is-active", isActive);

      if (trigger) {
        trigger.setAttribute("aria-expanded", isActive.toString());
      }

      if (panel) {
        setPanelState(panel, isActive, immediate);
      }

      if (mediaItem) {
        clearMediaTransition(mediaItem);
        mediaItem.classList.remove("is-leaving");

        if (isActive) {
          mediaItem.classList.add("is-active");
          mediaItem.setAttribute("aria-hidden", "false");
        } else if (
          !immediate &&
          index === currentActiveIndex &&
          mediaItem.classList.contains("is-active")
        ) {
          mediaItem.classList.remove("is-active");
          mediaItem.classList.add("is-leaving");
          mediaItem.setAttribute("aria-hidden", "true");
          mediaItem.__imageAccordionFadeTimeout = window.setTimeout(() => {
            mediaItem.classList.remove("is-leaving");
            mediaItem.__imageAccordionFadeTimeout = null;
          }, imageFadeDurationMs);
        } else {
          mediaItem.classList.remove("is-active");
          mediaItem.setAttribute("aria-hidden", "true");
        }
      }
    });

    currentActiveIndex = activeIndex;
  };

  const initialIndex = Math.max(
    0,
    items.findIndex((item) => item.classList.contains("is-active")),
  );

  setActiveItem(initialIndex, true);

  triggers.forEach((trigger, index) => {
    trigger.addEventListener("click", function () {
      setActiveItem(index);
    });

    trigger.addEventListener("keydown", function (event) {
      let nextIndex = null;

      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          nextIndex = (index + 1) % triggers.length;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          nextIndex = (index - 1 + triggers.length) % triggers.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = triggers.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      triggers[nextIndex].focus();
      setActiveItem(nextIndex);
    });
  });
}
